import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../Hook/AuthProvider";
import ScoreCard from "./ScoreCard";
import { reportTestQuestion, submitTest, fetchTestReview, fetchTestScorecard, startTest, heartbeatTest, cancelTest } from "../../services/testsService";
import "./TestScreen.css";

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const transformExamData = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.questions)
    ? data.questions
    : Array.isArray(data?.items)
    ? data.items
    : [];

  return list.map((q) => {
    const options = Object.entries(q.options || {}).map(([key, val]) => {
      const text = String(val);
      const letter = text.match(/^\(([a-zA-Z0-9]+)\)\s*/);
      return {
        id: letter ? letter[1].toUpperCase() : key,
        text: letter ? text.replace(letter[0], "") : text,
        rawText: text,
        rawKey: key,
      };
    });

    return {
      id: q.question_id,
      text: q.question?.stem || "",
      type: q.question?.type,
      options,
      statements: q.question?.statements || q.statements || [],
      instruction: q.question?.instruction || q.instruction || "",
    };
  });
};

const normalizeReviewQuestions = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.questions)
    ? data.questions
    : Array.isArray(data?.items)
    ? data.items
    : [];

  return list.map((q) => {
    const rawOptions = q.options || q.choices || {};
    const options = Object.entries(rawOptions).map(([key, val]) => {
      const text = String(val);
      const letter = text.match(/^\(([a-zA-Z0-9]+)\)\s*/);
      return {
        key,
        id: letter ? letter[1].toUpperCase() : key,
        text: letter ? text.replace(letter[0], "") : text,
      };
    });

    const idByKey = {};
    options.forEach((opt) => {
      if (opt.key) {
        idByKey[String(opt.key).replace(/[^a-z0-9]/gi, "").toLowerCase()] =
          opt.id;
      }
      idByKey[opt.id.toLowerCase()] = opt.id;
    });

    const normalizeAnswer = (value) => {
      if (value === undefined || value === null || value === "") return undefined;
      const str = String(value).trim();
      const stripped = str.replace(/[^a-z0-9]/gi, "").toLowerCase();
      const firstLetter = str.match(/^([a-zA-Z])/);
      const byKey =
        idByKey[stripped] || idByKey[stripped.replace(/^option/, "option-")];
      return byKey || (firstLetter ? firstLetter[1].toUpperCase() : undefined);
    };

    return {
      id: q.question_id ?? q.id,
      text: q.question?.stem || q.text || q.stem || "",
      type: q.question?.type || q.type,
      options,
      statements: q.question?.statements || q.statements || [],
      instruction: q.question?.instruction || q.instruction || "",
      answer: normalizeAnswer(
        q.answer ?? q.correct_option ?? q.solution?.correct_option ?? undefined
      ),
      selected: normalizeAnswer(
        q.selected_option ??
          q.user_answer ??
          q.user_selected ??
          q.selected_answer ??
          undefined
      ),
      explanation: q.explanation ?? q.solution?.explanation ?? "",
      is_correct: q.is_correct ?? undefined,
      is_flagged: q.is_flagged ?? 0,
      is_answered: q.is_answered ?? 0,
      is_opened: q.is_opened ?? 0,
      marks_awarded: q.marks_awarded ?? 0,
      negative_marks: q.negative_marks ?? 0,
    };
  });
};

const getReviewQuestionStatus = (question) => {
  if (!question) return "";
  if (question.is_correct === true) return "correct";
  if (question.is_flagged === 1) return "flagged";
  if (question.is_answered === 1) return "incorrect";
  if (question.is_opened === 1) return "unattempted";
  return "not-visited";
};

const loadFallback = async () => {
  try {
    const res = await fetch("/data/myTestData.json");
    const data = await res.json();
    return Array.isArray(data) ? data : data?.questions || data?.items || [];
  } catch (err) {
    console.error("Failed to load questions:", err);
    return [];
  }
};

const CountdownTimer = ({ initialSeconds, onExpire, onTick, paused }) => {
  const secondsRef = useRef(initialSeconds);
  const textRef = useRef(null);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    expiredRef.current = false;
    secondsRef.current = initialSeconds;
    if (textRef.current) {
      textRef.current.textContent = formatTime(secondsRef.current);
    }
    if (initialSeconds <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      if (onExpireRef.current) {
        onExpireRef.current();
      }
      return;
    }
    const timer = setInterval(() => {
      if (pausedRef.current) return;
      if (secondsRef.current > 0) {
        secondsRef.current -= 1;
        if (onTickRef.current) {
          onTickRef.current(secondsRef.current);
        }
      }
      if (textRef.current) {
        textRef.current.textContent = formatTime(secondsRef.current);
      }
      if (secondsRef.current <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        if (onExpireRef.current) {
          onExpireRef.current();
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [initialSeconds]);

  return (
    <div className="timer-display">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <span className="time-text" ref={textRef}>
        {formatTime(initialSeconds)}
      </span>
    </div>
  );
};

const ReviewSkeleton = ({ isScorecard = false }) => {
  if (isScorecard) {
    return (
      <div className="testscreen-container">
        {/* <div className="testscreen-header">
          <div className="header-left">
            <div className="skeleton skeleton-logo" />
            <div className="skeleton skeleton-sep" />
            <div className="skeleton skeleton-title" />
          </div>
        </div> */}

        <div className="testscreen-body">
          <div className="question-area">
            {/* <div className="review-toggle-bar">
              <div className="skeleton skeleton-toggle-btn" />
              <div className="skeleton skeleton-toggle-btn" />
              <div className="skeleton skeleton-toggle-btn" />
            </div> */}

            <div className="scorecard-box">
              {/* Top Bar */}
              <div className="scorecard-top-bar">
                <div className="skeleton sk-select" />
              </div>
              <div className="skeleton sk-hr" />

              {/* Heading */}
              <div className="skeleton sk-heading" />

              {/* Greeting */}
              <div className="scorecard-greeting">
                <h1 className="scorecard-user-name"><span className="skeleton sk-name" /></h1>
                <span className="scorecard-user-email"><span className="skeleton sk-email" /></span>
              </div>

              {/* Stats Container */}
              <div className="scorecard-stats-container">
                <div className="scorecard-rank-row">
                  {[1, 2, 3, 4].map((n) => (
                    <div className="scorecard-stat-card" key={n}>
                      <div className="skeleton sk-stat-icon" />
                      <div className="scorecard-stat-labels">
                        <span className="skeleton sk-stat-label" />
                        <span className="skeleton sk-stat-value" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="scorecard-stats-grid">
                  {[1, 2, 3, 4].map((n) => (
                    <div className="scorecard-stat-col" key={n}>
                      <div className="scorecard-stat-pair">
                        <span className="skeleton sk-pair-label" />
                        <span className="skeleton sk-pair-value" />
                      </div>
                      <div className="scorecard-stat-pair">
                        <span className="skeleton sk-pair-label" />
                        <span className="skeleton sk-pair-value" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3-Card Analytics Row Skeleton */}
              <div className="scorecard-analytics-row">
                {/* 1st: Paper Composition Skeleton */}
                <div className="scorecard-analytics-card">
                  <div className="skeleton sk-section-title" style={{ width: "120px", marginBottom: "14px" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <span className="skeleton sk-donut" />
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <div key={n} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span className="skeleton sk-legend-dot" />
                          <span className="skeleton sk-legend-text" style={{ width: "70%" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 2nd: Attempt Skeleton */}
                <div className="scorecard-analytics-card">
                  <div className="skeleton sk-section-title" style={{ width: "120px", marginBottom: "14px" }} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                    {[1, 2].map((n) => (
                      <div key={n} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span className="skeleton sk-attempt-icon" />
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <span className="skeleton sk-stat-label" style={{ width: "50px" }} />
                          <span className="skeleton sk-stat-value" style={{ width: "35px" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
                    <span className="skeleton sk-lottie" />
                  </div>
                  <div className="scorecard-attempt-tip-box" style={{ marginTop: "auto" }}>
                    <span className="skeleton sk-tip" />
                  </div>
                </div>

                {/* 3rd: Speed Skeleton */}
                <div className="scorecard-analytics-card" style={{ alignItems: "center" }}>
                  <div className="skeleton sk-section-title" style={{ width: "80px", alignSelf: "flex-start", marginBottom: "14px" }} />
                  <span className="skeleton sk-gauge" />
                </div>
              </div>

              {/* Complexity */}
              <div className="scorecard-complexity-section">
                <div className="scorecard-complexity-header-row">
                  <div className="skeleton sk-section-title" />
                  <div className="skeleton sk-filter" />
                </div>
                <div className="scorecard-complexity-cards">
                  {[1, 2, 3].map((n) => (
                    <div className="scorecard-complexity-card" key={n}>
                      <div className="scorecard-complexity-stats-row">
                        <div className="scorecard-complexity-stat-col-left">
                          <div className="skeleton sk-pair-label" />
                          <div className="skeleton sk-pair-value" />
                        </div>
                        <div className="scorecard-complexity-stat-col-divider" />
                        <div className="scorecard-complexity-stat-col-right">
                          <div className="skeleton sk-pair-label" />
                          <div className="skeleton sk-pair-value" />
                        </div>
                      </div>
                      <div className="scorecard-complexity-remarks">
                        <span className="skeleton sk-remark-line" />
                      </div>
                      <div className="scorecard-complexity-description">
                        <span className="skeleton sk-remark-line" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Module Level Performance */}
              <div className="scorecard-module-section">
                <div className="skeleton sk-section-title" />
                <div className="scorecard-module-table-wrap">
                  <table className="scorecard-module-table">
                    <thead>
                      <tr>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                          <th key={n}><span className="skeleton sk-th" /></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="scorecard-module-row-parent">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                          <td key={n}><span className="skeleton sk-td" /></td>
                        ))}
                      </tr>
                      {[1, 2, 3].map((n) => (
                        <tr className="scorecard-module-row-child" key={n}>
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((m) => (
                            <td key={m}><span className="skeleton sk-td" /></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Complexity vs Attempt Analysis */}
              <div className="scorecard-cva-section">
                <div className="skeleton sk-section-title" />
                <div className="scorecard-cva-table-wrap">
                  <table className="scorecard-cva-table">
                    <thead>
                      <tr>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <th key={n}><span className="skeleton sk-th" /></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4].map((n) => (
                        <tr key={n}>
                          {[1, 2, 3, 4, 5, 6].map((m) => (
                            <td key={m}><span className="skeleton sk-td" /></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bottom Note */}
              <div className="scorecard-bottom-note">
                <span className="skeleton sk-note" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="testscreen-container">
      <div className="testscreen-header">
        <div className="header-left">
          <div className="skeleton skeleton-logo" />
          <div className="skeleton skeleton-sep" />
          <div className="skeleton skeleton-title" />
        </div>
      </div>

      <div className="testscreen-body">
        <div className="question-area">
          <div className="review-toggle-bar">
            <div className="skeleton skeleton-toggle-btn" />
            <div className="skeleton skeleton-toggle-btn" />
            <div className="skeleton skeleton-toggle-btn" />
          </div>

          <div className="skeleton skeleton-question-block" />
          <div className="skeleton skeleton-question-block short" />
          <div className="skeleton skeleton-options" />
          <div className="skeleton skeleton-options short" />
          <div className="skeleton skeleton-options" />
        </div>

      <div className="palette-area">
        <div className="palette-box">
          <div className="skeleton skeleton-palette-title" />
          <div className="skeleton-grid">
            {Array.from({ length: 25 }).map((_, i) => (
              <div key={i} className="skeleton skeleton-grid-cell" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

// by sudz - 261004 - Forensic Watermark for Proctoring
const ProctorWatermark = React.memo(({ text }) => {
  const items = Array.from({ length: 36 }, (_, i) => i);
  return (
    <div className="proctor-watermark-overlay" aria-hidden="true">
      {items.map((idx) => (
        <span key={idx} className="proctor-watermark-stamp">
          {text}
        </span>
      ))}
    </div>
  );
});

const TestScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profileData } = useContext(AuthContext);
  const isReviewMode = location.pathname === "/test/review";

  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [guessTypes, setGuessTypes] = useState({});
  const [flags, setFlags] = useState({});
  const [visited, setVisited] = useState({ 0: true });

  const [timeSpent, setTimeSpent] = useState({});
  const questionStartRef = useRef(Date.now());

  const [showInstructions, setShowInstructions] = useState(false);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reviewViewMode, setReviewViewMode] = useState(() =>
    sessionStorage.getItem("test_review_view") === "scorecard"
      ? "scorecard"
      : "review"
  );
  const [scorecardData, setScorecardData] = useState(() => {
    try {
      const stored = sessionStorage.getItem("test_scorecard_data");
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      return null;
    }
  });
  const [scorecardFetching, setScorecardFetching] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [testId, setTestId] = useState(() => {
    return sessionStorage.getItem("test_id") || null;
  });
  const prevViewModeRef = useRef(null);
  const [testName, setTestName] = useState(
    sessionStorage.getItem("test_name") || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  // by sudz - 260926 - Multi-tab lock and session synchronization
  const [tabId] = useState(
    () => "tab_" + Math.random().toString(36).substring(2, 9) + "_" + Date.now()
  );
  const [tabBlocked, setTabBlocked] = useState(false);
  const [tabBlockedMsg, setTabBlockedMsg] = useState("");
  const channelRef = useRef(null);
  const [isTimeExpired, setIsTimeExpired] = useState(false);
  const [autoSubmitGraceSeconds, setAutoSubmitGraceSeconds] = useState(null);
  const [testDurationSeconds, setTestDurationSeconds] = useState(() => {
    const minutes = Number(sessionStorage.getItem("test_duration_minutes"));
    return minutes > 0 ? Math.round(minutes * 60) : 1500;
  });
  const remainingSecondsRef = useRef(testDurationSeconds);
  const handleTimerTick = (sec) => {
    remainingSecondsRef.current = sec;
  };

  // by sudz - 261004 - Proctoring State & Violations
  const MAX_PROCTOR_WARNINGS = 5;
  const [proctorViolations, setProctorViolations] = useState(() => {
    const storedId = sessionStorage.getItem("test_id");
    const saved = storedId ? sessionStorage.getItem(`mtr_proctor_violations_${storedId}`) : null;
    return saved ? Number(saved) || 0 : 0;
  });
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);
  const [showViolationModal, setShowViolationModal] = useState(false);
  const [violationNotice, setViolationNotice] = useState("");
  const [isProctorAutoSubmitting, setIsProctorAutoSubmitting] = useState(false);
  const [screenshotToast, setScreenshotToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  // by sudz - 261016 - Developer Tools detection & fullscreen lock
  const [devtoolsOpen, setDevtoolsOpen] = useState(false);
  const devtoolsOpenRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const enterFullscreenRef = useRef(() => {});

  const recordViolation = useCallback((reason) => {
    setProctorViolations((prev) => {
      const next = prev + 1;
      const activeId = testId || sessionStorage.getItem("test_id");
      if (activeId) {
        sessionStorage.setItem(`mtr_proctor_violations_${activeId}`, String(next));
      }
      if (next >= MAX_PROCTOR_WARNINGS) {
        setIsProctorAutoSubmitting(true);
        setShowViolationModal(false);
      }
      return next;
    });
    setViolationNotice(reason);
  }, [testId]);

  const showProctorToast = useCallback((msg) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setScreenshotToast(msg);
    toastTimeoutRef.current = setTimeout(() => {
      setScreenshotToast(null);
    }, 3500);
  }, []);

  const candidateIdentifier =
    profileData?.email ||
    profileData?.first_name ||
    localStorage.getItem("email") ||
    localStorage.getItem("user_id") ||
    "Candidate";
  const watermarkText = `${candidateIdentifier} • Test #${testId || ""} • ${new Date().toLocaleDateString()}`;

  // by sudz:260925 & 261004 - Test Integrity, Anti-Copy & Screenshot Prevention
  useEffect(() => {
    if (isReviewMode) return;

    const blockContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const blockCopy = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      e.preventDefault();
      return false;
    };

    const blockSelect = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      e.preventDefault();
      return false;
    };

    const blockDrag = (e) => {
      e.preventDefault();
      return false;
    };

    const wipeClipboard = () => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText("").catch(() => {});
      }
    };

    const blockKeyDown = (e) => {
      const isInput = e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA";
      const key = (e.key || "").toLowerCase();
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      // Windows PrintScreen
      if (e.key === "PrintScreen" || e.code === "PrintScreen" || key === "printscreen") {
        e.preventDefault();
        e.stopPropagation();
        wipeClipboard();
        recordViolation("PrintScreen / Screen capture detected.");
        showProctorToast("Screenshots are prohibited during active examination.");
        return false;
      }

      // macOS (Cmd+Shift+3/4/5/6) and Snipping Tool (Ctrl/Cmd+Shift+S)
      if (
        (isCtrlOrCmd && e.shiftKey && ["3", "4", "5", "6", "s"].includes(key)) ||
        (e.shiftKey && key === "s" && (e.metaKey || e.ctrlKey))
      ) {
        e.preventDefault();
        e.stopPropagation();
        wipeClipboard();
        recordViolation("Screen snip / capture shortcut detected.");
        showProctorToast("Screenshots are prohibited during active examination.");
        return false;
      }

      // Block copy, cut, print, save, view-source, select-all outside inputs
      if (isCtrlOrCmd && ["c", "x", "p", "s", "u"].includes(key) && !isInput) {
        e.preventDefault();
        return false;
      }
      if (isCtrlOrCmd && key === "a" && !isInput) {
        e.preventDefault();
        return false;
      }
      // Block DevTools shortcuts during active exam
      if (
        e.key === "F12" ||
        (isCtrlOrCmd && e.shiftKey && ["i", "j", "c"].includes(key)) ||
        (e.metaKey && e.altKey && ["i", "j", "c"].includes(key))
      ) {
        e.preventDefault();
        return false;
      }
    };

    const blockKeyUp = (e) => {
      const key = (e.key || "").toLowerCase();
      if (e.key === "PrintScreen" || e.code === "PrintScreen" || key === "printscreen") {
        e.preventDefault();
        e.stopPropagation();
        wipeClipboard();
        recordViolation("PrintScreen / Screen capture detected.");
        showProctorToast("Screenshots are prohibited during active examination.");
      }
    };

    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("copy", blockCopy);
    document.addEventListener("cut", blockCopy);
    document.addEventListener("selectstart", blockSelect);
    document.addEventListener("dragstart", blockDrag);
    document.addEventListener("keydown", blockKeyDown);
    document.addEventListener("keyup", blockKeyUp);

    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("copy", blockCopy);
      document.removeEventListener("cut", blockCopy);
      document.removeEventListener("selectstart", blockSelect);
      document.removeEventListener("dragstart", blockDrag);
      document.removeEventListener("keydown", blockKeyDown);
      document.removeEventListener("keyup", blockKeyUp);
    };
  }, [isReviewMode, testId, recordViolation, showProctorToast]);

  // by sudz - 261004 - Proctoring: Blur on focus loss / tab switch + Screen Obscuring
  useEffect(() => {
    if (isReviewMode) return;

    let blurTimer = null;

    const handleBlur = () => {
      blurTimer = setTimeout(() => {
        if (!isReviewMode && !submitting) {
          setIsWindowBlurred(true);
        }
      }, 120);
    };

    const handleFocus = () => {
      if (blurTimer) clearTimeout(blurTimer);
      setIsWindowBlurred((wasBlurred) => {
        if (wasBlurred) {
          recordViolation("Exam window lost focus (tab switch or external application).");
          setShowViolationModal(true);
        }
        return false;
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (!isReviewMode && !submitting) {
          setIsWindowBlurred(true);
        }
      } else {
        setIsWindowBlurred((wasBlurred) => {
          if (wasBlurred) {
            recordViolation("Exam tab was hidden or minimized.");
            setShowViolationModal(true);
          }
          return false;
        });
      }
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (blurTimer) clearTimeout(blurTimer);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isReviewMode, submitting, testId, recordViolation]);

  // by sudz - 261016 - Developer Tools detection: block test & pause timer until closed
  useEffect(() => {
    if (isReviewMode) return;

    const widthThreshold = 160;
    const heightThreshold = 200;
    const detect = () => {
      const widthDiff = window.outerWidth - window.innerWidth;
      const heightDiff = window.outerHeight - window.innerHeight;
      const open =
        widthDiff > widthThreshold || heightDiff > heightThreshold;
      if (open !== devtoolsOpenRef.current) {
        devtoolsOpenRef.current = open;
        setDevtoolsOpen(open);
        if (open) {
          recordViolation("Developer tools / network inspector detected.");
        }
      }
    };

    detect();
    const interval = setInterval(detect, 800);
    window.addEventListener("resize", detect);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", detect);
    };
  }, [isReviewMode, recordViolation]);

  // by sudz - 261016 - Enter fullscreen on test start (hides docked devtools panels)
  useEffect(() => {
    if (isReviewMode) return;

    const enterFullscreen = () => {
      const el = document.documentElement;
      if (el.requestFullscreen && !document.fullscreenElement) {
        el.requestFullscreen().catch(() => {});
      }
    };
    enterFullscreenRef.current = enterFullscreen;

    const onFullscreenChange = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    onFullscreenChange();

    // Retry on first user gesture (browsers require a gesture to grant fullscreen)
    const onFirstInteraction = () => {
      if (!document.fullscreenElement) enterFullscreen();
    };
    window.addEventListener("pointerdown", onFirstInteraction, { once: true });
    window.addEventListener("keydown", onFirstInteraction, { once: true });

    enterFullscreen();

    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      window.removeEventListener("pointerdown", onFirstInteraction);
      window.removeEventListener("keydown", onFirstInteraction);
    };
  }, [isReviewMode]);

  // by sudz - 260926 - Phase 4: Session Resilience, Safe Exits & Navigation Guards
  // Save active exam progress to sessionStorage
  const saveSessionProgress = () => {
    if (isReviewMode || !testId) return;
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);
    const updatedTimeSpent = { ...timeSpent };
    if (elapsed > 0) {
      updatedTimeSpent[currentQIndex] =
        (updatedTimeSpent[currentQIndex] || 0) + elapsed;
    }
    const sessionPayload = {
      testId,
      currentQIndex,
      answers,
      guessTypes,
      flags,
      visited,
      timeSpent: updatedTimeSpent,
      remainingSeconds: remainingSecondsRef.current,
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(
        `test_progress_${testId}`,
        JSON.stringify(sessionPayload)
      );
    } catch (err) {
      console.error("Failed to save session progress:", err);
    }
  };

  // by sudz - 260926 - Cancel in-progress test and release multi-tab lock upon exit
  const handleExitTest = async (clearProgress = false) => {
    const userId = localStorage.getItem("user_id") || "";
    try {
      if (userId && testId) {
        await cancelTest(testId, userId);
      }
    } catch (e) {
      console.error("Failed to cancel test:", e);
    }
    const lockDataStr = localStorage.getItem("mtr_active_test_lock");
    if (lockDataStr) {
      try {
        const lockData = JSON.parse(lockDataStr);
        if (lockData.tabId === tabId) {
          localStorage.removeItem("mtr_active_test_lock");
        }
      } catch (e) {}
    }
    try {
      if (channelRef.current) {
        channelRef.current.postMessage({ type: "RELEASE_LOCK", tabId, testId: String(testId) });
      }
    } catch (e) {
      // Channel may already be closed
    }
    sessionStorage.removeItem("test_exam_data");
    if (testId) {
      sessionStorage.removeItem(`test_progress_${testId}`);
    }
    setShowExitModal(false);
    navigate("/tests");
  };

  // by sudz - 260926 - Multi-tab lock and backend session initiation
  useEffect(() => {
    if (isReviewMode || !testId) return;

    let channel = null;
    try {
      channel = new BroadcastChannel("mtr_active_test_channel");
      channelRef.current = channel;
    } catch (e) {
      console.warn("BroadcastChannel not supported in this environment");
    }

    const userId = localStorage.getItem("user_id") || "";

    const checkLock = () => {
      const lockDataStr = localStorage.getItem("mtr_active_test_lock");
      if (lockDataStr) {
        try {
          const lockData = JSON.parse(lockDataStr);
          if (
            lockData.tabId &&
            lockData.tabId !== tabId &&
            Date.now() - lockData.timestamp < 12000
          ) {
            setTabBlocked(true);
            setTabBlockedMsg(
              "This test is currently open in another browser tab. Simultaneous test taking in multiple tabs is not permitted."
            );
            return false;
          }
        } catch (e) {}
      }

      // Claim lock for this tab
      const currentLock = {
        tabId,
        testId: String(testId),
        userId,
        timestamp: Date.now(),
      };
      localStorage.setItem("mtr_active_test_lock", JSON.stringify(currentLock));
      if (channel) {
        channel.postMessage({
          type: "ACQUIRE_LOCK",
          tabId,
          testId: String(testId),
          userId,
        });
      }
      setTabBlocked(false);
      return true;
    };

    const handleChannelMessage = (event) => {
      const data = event.data;
      if (!data) return;
      if (data.type === "ACQUIRE_LOCK" && data.tabId !== tabId) {
        setTabBlocked(true);
        setTabBlockedMsg(
          "This test was resumed or opened in another tab. Active test taking has been switched to the other tab."
        );
      }
    };

    if (channel) {
      channel.addEventListener("message", handleChannelMessage);
    }

    checkLock();

    // Call backend startTest to create/resume IN_PROGRESS attempt
    if (userId) {
      startTest(testId, userId)
        .catch((err) => {
          if (
            err?.detail &&
            typeof err.detail === "string" &&
            err.detail.includes("in progress")
          ) {
            setTabBlocked(true);
            setTabBlockedMsg(err.detail);
          }
        });
    }

    return () => {
      if (channel) {
        channel.removeEventListener("message", handleChannelMessage);
        try {
          channel.close();
        } catch (e) {}
        if (channelRef.current === channel) {
          channelRef.current = null;
        }
      }
      const lockDataStr = localStorage.getItem("mtr_active_test_lock");
      if (lockDataStr) {
        try {
          const lockData = JSON.parse(lockDataStr);
          if (lockData.tabId === tabId) {
            localStorage.removeItem("mtr_active_test_lock");
          }
        } catch (e) {}
      }
    };
  }, [testId, isReviewMode, tabId]);

  // Auto-save on answer, flag, guess, visited, or current question index changes
  useEffect(() => {
    if (isReviewMode || !hasLoaded || !testId || tabBlocked) return;
    saveSessionProgress();
  }, [answers, guessTypes, flags, visited, currentQIndex, tabBlocked]);

  // by sudz - 260926 - Periodic heartbeat: refresh lock timestamp and backend updated_at every 5 seconds
  useEffect(() => {
    if (isReviewMode || !hasLoaded || !testId || tabBlocked) return;
    const userId = localStorage.getItem("user_id") || "";
    const interval = setInterval(() => {
      saveSessionProgress();

      localStorage.setItem(
        "mtr_active_test_lock",
        JSON.stringify({
          tabId,
          testId: String(testId),
          userId,
          timestamp: Date.now(),
        })
      );

      if (userId) {
        const totalSecs = Object.values(timeSpent).reduce((a, b) => a + b, 0);
        heartbeatTest(testId, userId, totalSecs);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isReviewMode, hasLoaded, testId, tabBlocked, answers, guessTypes, flags, visited, currentQIndex, timeSpent, tabId]);

  // Navigation Guard 1: Browser beforeunload (prevent accidental tab close / page reload)
  useEffect(() => {
    if (isReviewMode) return;

    const handleBeforeUnload = (e) => {
      if (submitting) return;
      saveSessionProgress();
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isReviewMode, submitting, answers, guessTypes, flags, visited, currentQIndex, testId]);

  // Navigation Guard 2: Browser Back button trap (popstate)
  useEffect(() => {
    if (isReviewMode) return;

    window.history.pushState({ testScreenGuard: true }, "", window.location.href);

    const handlePopState = () => {
      window.history.pushState({ testScreenGuard: true }, "", window.location.href);
      setShowExitModal(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isReviewMode]);

  useEffect(() => {
    if (isReviewMode) {
      const loadReview = async () => {
        try {
          const userId = localStorage.getItem("user_id") || "";
          const reviewTestId =
            sessionStorage.getItem("test_id") ||
            (() => {
              let testId = null;
              try {
                const payload = JSON.parse(
                  sessionStorage.getItem("test_exam_data") || "{}"
                );
                testId = payload?.testId || null;
              } catch (err) {
                console.error("Failed to parse test exam data:", err);
              }
              return testId;
            })();
          if (reviewTestId) {
            setTestId(reviewTestId);
            const data = await fetchTestReview(reviewTestId, userId);
            const reviewQuestions = normalizeReviewQuestions(data);
            setQuestions(
              reviewQuestions.length > 0 ? reviewQuestions : (await loadFallback())
            );
          } else {
            setQuestions(await loadFallback());
          }
        } catch (err) {
          console.error("Failed to load review data:", err);
          setQuestions(await loadFallback());
        } finally {
          setHasLoaded(true);
        }
      };
      loadReview();
      return;
    }

    const stored = sessionStorage.getItem("test_exam_data");
    if (stored) {
      let payload = null;
      try {
        payload = JSON.parse(stored);
      } catch (err) {
        console.error("Failed to parse test exam data:", err);
      }

      const examData = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.questions)
        ? payload.questions
        : Array.isArray(payload?.items)
        ? payload.items
        : [];

      if (Array.isArray(examData) && examData.length > 0) {
        const resolvedTestId =
          (payload && typeof payload === "object" && !Array.isArray(payload) && payload.testId) ||
          sessionStorage.getItem("test_id") ||
          null;

        if (resolvedTestId) {
          setTestId(resolvedTestId);
        }

        if (payload?.testName) {
          setTestName(payload.testName);
        }

        let initialSec = 1500;
        const testOverrideSec = Number(sessionStorage.getItem("test_duration_seconds"));
        if (testOverrideSec > 0) {
          initialSec = testOverrideSec;
        } else if (payload?.duration_minutes && Number(payload.duration_minutes) > 0) {
          initialSec = Math.round(Number(payload.duration_minutes) * 60);
        }

        // Restore saved session progress if available
        if (resolvedTestId) {
          try {
            const rawProgress = sessionStorage.getItem(`test_progress_${resolvedTestId}`);
            if (rawProgress) {
              const saved = JSON.parse(rawProgress);
              if (saved) {
                if (saved.answers) setAnswers(saved.answers);
                if (saved.guessTypes) setGuessTypes(saved.guessTypes);
                if (saved.flags) setFlags(saved.flags);
                if (saved.visited) setVisited(saved.visited);
                if (saved.timeSpent) setTimeSpent(saved.timeSpent);
                if (
                  typeof saved.currentQIndex === "number" &&
                  saved.currentQIndex >= 0 &&
                  saved.currentQIndex < examData.length
                ) {
                  setCurrentQIndex(saved.currentQIndex);
                }
                if (typeof saved.remainingSeconds === "number") {
                  const elapsedSinceSave = saved.timestamp
                    ? Math.max(0, Math.floor((Date.now() - saved.timestamp) / 1000))
                    : 0;
                  initialSec = Math.max(0, saved.remainingSeconds - elapsedSinceSave);
                }
              }
            }
          } catch (e) {
            console.error("Failed to restore saved progress:", e);
          }
        }

        setTestDurationSeconds(initialSec);
        remainingSecondsRef.current = initialSec;
        setQuestions(transformExamData(examData));
        setHasLoaded(true);
        return;
      }
    }

    (async () => {
      setQuestions(await loadFallback());
      setHasLoaded(true);
    })();
  }, [isReviewMode]);

  useEffect(() => {
    if (isReviewMode && reviewViewMode === "scorecard" && testId) {
      if (prevViewModeRef.current !== "scorecard") {
        prevViewModeRef.current = "scorecard";
        const loadScorecard = async () => {
          try {
            setScorecardFetching(true);
            const userId = localStorage.getItem("user_id") || "";
            const data = await fetchTestScorecard(testId, userId);
            setScorecardData(data);
            sessionStorage.setItem(
              "test_scorecard_data",
              JSON.stringify(data)
            );
          } catch (err) {
            console.error("Failed to load scorecard:", err);
          } finally {
            setScorecardFetching(false);
          }
        };
        loadScorecard();
      }
    } else {
      prevViewModeRef.current = reviewViewMode;
    }
  }, [isReviewMode, reviewViewMode, testId]);

  const handleOptionSelect = (optionId) => {
    if (isReviewMode) return;
    setAnswers({ ...answers, [currentQIndex]: optionId });
  };

  const handleGuessTypeSelect = (guessType) => {
    setGuessTypes({ ...guessTypes, [currentQIndex]: guessType });
  };

  const currentQ = questions[currentQIndex];

  const questionStem =
    currentQ?.text || currentQ?.stem || currentQ?.question?.stem || "";
  const questionStatements = Array.isArray(currentQ?.statements)
    ? currentQ.statements
    : Array.isArray(currentQ?.question?.statements)
    ? currentQ.question.statements
    : [];
  const questionInstruction = String(
    currentQ?.instruction || currentQ?.question?.instruction || ""
  ).trim();

  const accumulateElapsed = () => {
    if (isReviewMode) return 0;
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);
    questionStartRef.current = Date.now();
    if (elapsed > 0) {
      setTimeSpent((prev) => ({
        ...prev,
        [currentQIndex]: (prev[currentQIndex] || 0) + elapsed,
      }));
    }
    return elapsed;
  };

  const handleNext = () => {
    if (currentQIndex < questions.length - 1) {
      accumulateElapsed();
      const nextIndex = currentQIndex + 1;
      setCurrentQIndex(nextIndex);
      if (!isReviewMode) {
        setVisited({ ...visited, [nextIndex]: true });
      }
    }
  };

  const handlePrev = () => {
    if (currentQIndex > 0) {
      accumulateElapsed();
      const prevIndex = currentQIndex - 1;
      setCurrentQIndex(prevIndex);
      if (!isReviewMode) {
        setVisited({ ...visited, [prevIndex]: true });
      }
    }
  };

  const goToQuestion = (index) => {
    accumulateElapsed();
    setCurrentQIndex(index);
    if (!isReviewMode) {
      setVisited({ ...visited, [index]: true });
    }
  };

  const qGridRef = useRef(null);
  useEffect(() => {
    const activeBtn = qGridRef.current?.querySelector(".q-grid-btn.active");
    if (activeBtn) {
      activeBtn.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [currentQIndex, questions]);

  const toggleFlag = () => {
    setFlags({ ...flags, [currentQIndex]: !flags[currentQIndex] });
    handleNext();
  };

  const clearAnswer = () => {
    const newAnswers = { ...answers };
    delete newAnswers[currentQIndex];
    setAnswers(newAnswers);
  };

  const clearGuessType = () => {
    const newGuess = { ...guessTypes };
    delete newGuess[currentQIndex];
    setGuessTypes(newGuess);
  };

  const handleSubmit = async () => {
    const activeTestId = testId || sessionStorage.getItem("test_id");
    if (submitting || !activeTestId) return;
    const finalTimeSpent = { ...timeSpent };
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);
    questionStartRef.current = Date.now();
    if (elapsed > 0) {
      finalTimeSpent[currentQIndex] =
        (finalTimeSpent[currentQIndex] || 0) + elapsed;
    }
    setSubmitting(true);
    try {
      const userId = localStorage.getItem("user_id") || "";
      const guessTypeValue = (type) => {
        if (type === "confident") return 2;
        if (type === "smart") return 1;
        return 0;
      };
      const answersPayload = questions.map((q, index) => {
        const selectedId = answers[index] || "";
        const matchedOption = q.options.find(o => o.id === selectedId);
        return {
          question_id: q.id,
          selected_option: matchedOption
            ? matchedOption.rawKey.toUpperCase()
            : selectedId,
          is_opened: visited[index] ? 1 : 0,
          is_answered: answers[index] ? 1 : 0,
          is_flagged: flags[index] ? 1 : 0,
          time_spent_seconds: finalTimeSpent[index] || 0,
          confident_ans: guessTypeValue(guessTypes[index]),
        };
      });

      const totalSeconds = Object.values(finalTimeSpent).reduce(
        (acc, sec) => acc + sec,
        0
      );

      await submitTest(activeTestId, userId, {
        time_spent_seconds: totalSeconds,
        answers: answersPayload,
      });

      // by sudz - 260926 - Release multi-tab lock upon submission
      const lockDataStr = localStorage.getItem("mtr_active_test_lock");
      if (lockDataStr) {
        try {
          const lockData = JSON.parse(lockDataStr);
          if (lockData.tabId === tabId) {
            localStorage.removeItem("mtr_active_test_lock");
          }
        } catch (e) {}
      }
      try {
        if (channelRef.current) {
          channelRef.current.postMessage({ type: "RELEASE_LOCK", tabId, testId: String(activeTestId) });
        }
      } catch (e) {}

      sessionStorage.removeItem("test_exam_data");
      sessionStorage.removeItem("test_duration_seconds");
      if (activeTestId) {
        sessionStorage.removeItem(`test_progress_${activeTestId}`);
        sessionStorage.removeItem(`mtr_proctor_violations_${activeTestId}`);
      }
      setShowSubmitModal(false);
      navigate("/test/success");
    } catch (error) {
      console.error("Failed to submit test:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  // by sudz - 261004 - Auto-submit after 5 proctoring warnings
  useEffect(() => {
    if (!isProctorAutoSubmitting || submitting) return;

    // Gracefully submit after 3.5s allowing user to read subtle notification
    const timer = setTimeout(() => {
      if (handleSubmitRef.current) {
        handleSubmitRef.current();
      }
    }, 3500);

    return () => clearTimeout(timer);
  }, [isProctorAutoSubmitting, submitting]);

  // by sudz - 260926 - Final Submission Action on Timer Expiry
  const handleTimeExpired = () => {
    setIsTimeExpired(true);
    accumulateElapsed();
    setShowExitModal(false);
    setShowQuestionPaper(false);
    setShowInstructions(false);
    setShowReportModal(false);
    setShowSubmitModal(true);
    setAutoSubmitGraceSeconds(15);
  };

  useEffect(() => {
    if (autoSubmitGraceSeconds === null) return;
    if (autoSubmitGraceSeconds <= 0) {
      handleSubmit();
      return;
    }
    const timer = setTimeout(() => {
      setAutoSubmitGraceSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [autoSubmitGraceSeconds]);

  const getQuestionStatus = (index) => {
    const isAnswered = !!answers[index];
    const isFlagged = !!flags[index];
    const isVisited = !!visited[index];

    if (isAnswered && isFlagged) return "answered-marked";
    if (isAnswered) return "answered";
    if (isFlagged) return "marked";
    if (!isVisited) return "not-visited";
    return "not-answered";
  };

  let countAnswered = 0;
  let countNotAnswered = 0;
  let countMarked = 0;
  let countNotVisited = 0;
  let countAnsweredMarked = 0;

  let countCorrect = 0;
  let countIncorrect = 0;
  let countUnattempted = 0;
  let countFlagged = 0;
  let countReviewNotVisited = 0;

  if (!isReviewMode) {
    for (let i = 0; i < questions.length; i++) {
      const stat = getQuestionStatus(i);
      if (stat === "answered") countAnswered++;
      else if (stat === "not-answered") countNotAnswered++;
      else if (stat === "marked") countMarked++;
      else if (stat === "not-visited") countNotVisited++;
      else if (stat === "answered-marked") countAnsweredMarked++;
    }
  } else {
    for (let i = 0; i < questions.length; i++) {
      const stat = getReviewQuestionStatus(questions[i]);
      if (stat === "correct") countCorrect++;
      else if (stat === "incorrect") countIncorrect++;
      else if (stat === "unattempted") countUnattempted++;
      else if (stat === "flagged") countFlagged++;
      else if (stat === "not-visited") countReviewNotVisited++;
    }
  }

  if (questions.length === 0 && !(isReviewMode && reviewViewMode === "scorecard")) {
    if (!hasLoaded) {
      return <ReviewSkeleton />;
    }
    return (
      <div className="testscreen-container">
        No questions available.
      </div>
    );
  }

  const scorecardLoading =
    isReviewMode && reviewViewMode === "scorecard" && (scorecardFetching || !scorecardData);

  return (
    // by sudz:260925 - Anti-Copy & ContextMenu protection on active test container
    <div
      className={`testscreen-container ${!isReviewMode ? "test-mode-active" : ""}`}
      onContextMenu={(e) => {
        if (!isReviewMode) e.preventDefault();
      }}
      onCopy={(e) => {
        if (!isReviewMode) e.preventDefault();
      }}
      onCut={(e) => {
        if (!isReviewMode) e.preventDefault();
      }}
      onDragStart={(e) => {
        if (!isReviewMode) e.preventDefault();
      }}
    >
      {/* by sudz - 261004 - Proctoring Overlays (Watermark, Screenshot Toast & Print Shield) */}
      {!isReviewMode && <ProctorWatermark text={watermarkText} />}
      {!isReviewMode && screenshotToast && (
        <div className="proctor-toast" role="alert">
          <span className="proctor-toast-icon">🛡️</span>
          <span className="proctor-toast-message">{screenshotToast}</span>
        </div>
      )}
      <div className="proctor-print-shield" style={{ display: "none" }}>
        Printing examination materials is strictly prohibited.
      </div>

      <div className="testscreen-header">
        <div className="header-left">
          <img
            src="/assets/images/logo/SVG/Logo-Org.svg"
            alt="Yooki"
            className="yooki-logo"
          />
          <span className="separator">|</span>
          <span className="exam-title-header">
            {isReviewMode && testName ? `Review Test - ${testName}` : testName}
          </span>
        </div>
        <div className="header-right">
          {!isReviewMode && (
            <div className="header-timer-actions">
              <div className="proctor-badge" title="Active Proctoring Enabled">
                <span className="proctor-badge-dot" />
                <span className="proctor-badge-label">Proctored</span>
                {proctorViolations > 0 && (
                  <span
                    className={`proctor-violation-badge ${proctorViolations >= 4 ? "critical" : ""}`}
                  >
                    {proctorViolations}/5 warnings
                  </span>
                )}
              </div>
              <CountdownTimer
                initialSeconds={testDurationSeconds}
                onExpire={handleTimeExpired}
                onTick={handleTimerTick}
                paused={devtoolsOpen}
              />
              <button
                type="button"
                className="exit-header-btn"
                onClick={() => setShowExitModal(true)}
              >
                Exit Test
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="testscreen-body">
        <div className="question-area">
          {isReviewMode && (
            <div className="review-toggle-bar">
              <button
                type="button"
                className="review-toggle-btn review-home-toggle-btn"
                onClick={() => navigate("/tests")}
              >
                Home
              </button>
              <button
                type="button"
                className={`review-toggle-btn ${reviewViewMode === "review" ? "active" : ""}`}
                onClick={() => setReviewViewMode("review")}
              >
                Review Test
              </button>
              <button
                type="button"
                className={`review-toggle-btn ${reviewViewMode === "scorecard" ? "active" : ""}`}
                onClick={() => setReviewViewMode("scorecard")}
              >
                Score Card
              </button>
            </div>
          )}

          {isReviewMode && reviewViewMode === "scorecard" ? (
            scorecardLoading ? (
              <ReviewSkeleton isScorecard />
            ) : (
              <ScoreCard
                userName={
                  profileData?.first_name ||
                  localStorage.getItem("email")?.split("@")[0] ||
                  "Candidate"
                }
                questions={questions}
                answers={answers}
                guessTypes={guessTypes}
                testName={testName}
                scorecardData={scorecardData}
              />
            )
          ) : (
            <div className="question-content-box">
              <div className="qa-top-bar">
                <div className="qa-top-left">
                  {!isReviewMode && (
                    <button
                      type="button"
                      className="qa-back-btn"
                      onClick={() => setShowExitModal(true)}
                      aria-label="Exit Assessment"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      <span>Exit</span>
                    </button>
                  )}
                  <div className="section-selector">
                    <span className="sec-label">Section</span>
                    <div className="sec-badge">{testName}</div>
                  </div>
                </div>
                <div className="view-in">
                  <span className="lang-label">View In:</span>
                  <select className="lang-select">
                    <option>English</option>
                  </select>
                </div>
              </div>
              <div className="question-body-scroll">
              <div className="qc-header">
                <div className="qc-left">
                  <span className="q-num">Question {currentQIndex + 1}</span>
                  {!isReviewMode && (
                    <>
                      <span className="q-mark positive">
                      <img src="/assets/images/right-mark.svg" alt="" />
                        2
                      </span>
                      <span className="q-mark negative">
                        <img src="/assets/images/wrong-mark.svg" alt="" />
                        -0.66
                      </span>
                    </>
                  )}
                </div>
                {!isReviewMode && (
                  <div className="qc-right">
                    <button
                      type="button"
                      className="text-btn flag-btn"
                      onClick={toggleFlag}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill={flags[currentQIndex] ? "#3b82f6" : "none"}
                        stroke="#3b82f6"
                        strokeWidth="2"
                      >
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                        <line x1="4" y1="22" x2="4" y2="15"></line>
                      </svg>
                      {flags[currentQIndex] ? "Unflag" : "Flag for Review"}
                    </button>
                    <button
                      type="button"
                      className="text-btn clear-ans-btn"
                      onClick={clearAnswer}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                      </svg>
                      Clear Answer
                    </button>
                  </div>
                )}
              </div>

              {/* <div className="q-text">
                {currentQ.text.split("\n").map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div> */}
              <div className="question-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: questionStem,
                  }}
                />
                {questionStatements.length > 0 && (
                  <div className="question-statements">
                    {questionStatements.map((stmt, i) => (
                      <div key={i} className="question-statement">
                        <span className="question-statement-index">{i + 1}.</span>
                        <span
                          className="question-statement-text"
                          dangerouslySetInnerHTML={{
                            __html: String(stmt ?? ""),
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {questionInstruction !== "" && (
                  <div
                    className="question-instruction"
                    dangerouslySetInnerHTML={{ __html: questionInstruction }}
                  />
                )}
              </div>

              <div className="options-list">
                {currentQ.options.map((opt) => {
                  const isCorrect = opt.id === currentQ.answer;
                  if (isReviewMode) {
                    const isSelected = opt.id === currentQ.selected;
                    const optionClass =
                      isSelected && !isCorrect
                        ? "review-wrong"
                        : isCorrect
                        ? "review-correct"
                        : "";
                    return (
                      <div
                        key={opt.id}
                        className={`option-item ${optionClass}`}
                      >
                        <span className="opt-letter">({opt.id})</span>
                        <span className="opt-text">{opt.text}</span>
                        {isCorrect && (
                          <span className="correct-badge">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            Correct
                          </span>
                        )}
                        {isSelected && !isCorrect && (
                          <span className="wrong-badge">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                            Your Answer
                          </span>
                        )}
                      </div>
                    );
                  }
                  return (
                    <label
                      key={opt.id}
                      className={`option-item ${answers[currentQIndex] === opt.id ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name={`q-${currentQIndex}`}
                        value={opt.id}
                        checked={answers[currentQIndex] === opt.id}
                        onChange={() => handleOptionSelect(opt.id)}
                      />
                      <span className="opt-letter">({opt.id})</span>
                      <span className="opt-text">{opt.text}</span>
                    </label>
                  );
                })}
              </div>

              {isReviewMode && currentQ.explanation && (
                <div className="review-explanation-box">
                  <div className="review-exp-label">Explanation</div>
                  <p dangerouslySetInnerHTML={{ __html: currentQ.explanation }} />
                </div>
              )}

              {!isReviewMode && (
                  <div className="guess-type-section">
                            <div className="gt-header">
                                <strong>How did you arrive at the answer?</strong>
                                <button type="button" className="text-btn clear-guess-btn" onClick={clearGuessType}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h18v18H3zM3 9h18M9 21V9" /></svg>
                                    Clear Guess Type
                                </button>
                            </div>
                            <div className="gt-options">
                                <label className="gt-label">
                                    <input type="radio" name={`gt-${currentQIndex}`} checked={guessTypes[currentQIndex] === 'confident'} onChange={() => handleGuessTypeSelect('confident')} /> Confident
                                </label>
                                <label className="gt-label">
                                    <input type="radio" name={`gt-${currentQIndex}`} checked={guessTypes[currentQIndex] === 'smart'} onChange={() => handleGuessTypeSelect('smart')} /> Smart Guess
                                </label>
                                <label className="gt-label">
                                    <input type="radio" name={`gt-${currentQIndex}`} checked={guessTypes[currentQIndex] === 'blind'} onChange={() => handleGuessTypeSelect('blind')} /> Blind Guess
                                </label>
                            </div>
                        </div>
              )}
              </div>

              <div className="qc-footer">
                <button
                  type="button"
                  className="report-btn"
                  onClick={() => {
                    setReportType("");
                    setReportDescription("");
                    setShowReportModal(true);
                  }}
                >
                  Report Question
                </button>
                <div className="qc-footer-right">
                  {currentQIndex > 0 && (
                    <button
                      type="button"
                      className="prev-btn"
                      onClick={handlePrev}
                    >
                      &lsaquo; Previous Question
                    </button>
                  )}
                  {currentQIndex < questions.length - 1 && (
                    <button
                      type="button"
                      className="next-btn"
                      onClick={handleNext}
                    >
                      Next Question &rsaquo;
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {!(isReviewMode && reviewViewMode === "scorecard") && (
          <div className="palette-area">
            <div className="palette-box">
              <h3 className="palette-title">QUESTION PALETTE</h3>

              <fieldset className="legend-box">
                <legend>Legend</legend>
                <div className="legend-grid">
                  {isReviewMode ? (
                    <>
                      <div className="legend-item">
                        <span className="status-badge answered">
                          {countCorrect}
                        </span>{" "}
                        Correct
                      </div>
                      <div className="legend-item">
                        <span className="status-badge not-answered">
                          {countIncorrect}
                        </span>{" "}
                        Incorrect
                      </div>
                      <div className="legend-item">
                        <span className="status-badge not-visited">
                          {countUnattempted}
                        </span>{" "}
                        Unattempted
                      </div>
                      <div className="legend-item">
                        <span className="status-badge marked">
                          {countFlagged}
                        </span>{" "}
                        Flagged
                      </div>
                      <div className="legend-item fl-width">
                        <span className="status-badge not-visited">
                          {countReviewNotVisited}
                        </span>{" "}
                        Not Visited
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="legend-item">
                        <span className="status-badge answered">
                          {countAnswered}
                        </span>{" "}
                        Answered
                      </div>
                      <div className="legend-item">
                        <span className="status-badge not-answered">
                          {countNotAnswered}
                        </span>{" "}
                        Not Answered
                      </div>
                      <div className="legend-item">
                        <span className="status-badge marked">{countMarked}</span>{" "}
                        Marked
                      </div>
                      <div className="legend-item">
                        <span className="status-badge not-visited">
                          {countNotVisited}
                        </span>{" "}
                        Not Visited
                      </div>
                      <div className="legend-item fl-width">
                        <span className="status-badge answered-marked">
                          {countAnsweredMarked}
                        </span>{" "}
                        Answered and Marked for review
                      </div>
                    </>
                  )}
                </div>
              </fieldset>

              <div className="q-grid" ref={qGridRef}>
                {questions.map((q, idx) => {
                  const stat = isReviewMode ? getReviewQuestionStatus(q) : getQuestionStatus(idx);
                  return (
                    <button
                      type="button"
                      key={idx}
                      className={`q-grid-btn ${stat} ${currentQIndex === idx ? "active" : ""}`}
                      onClick={() => goToQuestion(idx)}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>

              {!isReviewMode && (
                <>
                  <div className="palette-actions">
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => setShowInstructions(true)}
                    >
                      Instructions
                    </button>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => setShowQuestionPaper(true)}
                    >
                      Question Paper
                    </button>
                  </div>

                  <button
                    type="button"
                    className="submit-test-btn"
                    onClick={() => setShowSubmitModal(true)}
                  >
                    Submit Test
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {!isReviewMode && showInstructions && (
        <div className="modal-overlay">
          <div className="modal-content instructions-modal">
            <button
              type="button"
              className="modal-close"
              onClick={() => setShowInstructions(false)}
            >
              &times;
            </button>
            <h2 className="modal-title">Instructions :</h2>
            <ul className="modal-list">
              <li>
                After you select the correct option for the question, click on
                Next. This is mandatory for submitting your answer.
              </li>
              <li>
                If you happen to choose the option and move to the next question
                from the Question Palette on the right, the answer will not be
                submitted. Such questions will be considered as not answered and
                marked in red.
              </li>
              <li>
                The questions which are not attempted at all are also marked in
                red.
              </li>
              <li>
                Question Palette will show you questions answered, not answered,
                marked, not visited, answered & marked for review.
              </li>
              <li>
                After you finish answering your question, it is mandatory to
                click on Submit, else the test remains in an unfinished state
                and the reporting will not be accurate.
              </li>
              <li>
                After finishing the test, you'll be able to see your scores and
                solutions to the questions
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* by sudz - 260926 - Question Paper Modal (compacted layout) */}
      {!isReviewMode && showQuestionPaper && (
        <div className="modal-overlay">
          <div className="testscreen-paper-modal">
            <div className="testscreen-paper-modal-header">
              <div className="testscreen-paper-modal-header-top">
                <h2 className="modal-title paper-title">Question Paper</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setShowQuestionPaper(false)}
                >
                  &times;
                </button>
              </div>
              <div className="paper-top-bar">
                <div className="section-selector">
                  <span className="sec-label">Section</span>
                  <div className="sec-badge">{testName}</div>
                </div>
                <div className="view-in">
                  <span className="lang-label">View In:</span>
                  <select className="lang-select">
                    <option>English</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="testscreen-paper-modal-inner">
              <div className="paper-questions">
                {questions.map((q, i) => (
                  <div key={i} className="paper-q-row">
                    <div className="paper-q-num">Q{i + 1}.</div>
                    <div className="paper-q-body">
                      <div
                        style={{ whiteSpace: "pre-line" }}
                        dangerouslySetInnerHTML={{ __html: q.text }}
                      />
                      <div className="paper-opts-list">
                        {q.options.map((opt) => (
                          <div key={opt.id} className="paper-opt">
                            ({opt.id.toLowerCase()}) {opt.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isReviewMode && showSubmitModal && (
        <div className="modal-overlay">
          <div className={`modal-content submit-modal ${isTimeExpired ? "time-expired-modal" : ""}`}>
            {!isTimeExpired && (
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowSubmitModal(false)}
              >
                &times;
              </button>
            )}

            <div className="submit-modal-header">
              <div className={`submit-modal-icon-wrap ${isTimeExpired ? "time-expired-icon" : ""}`}>
                {isTimeExpired ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                )}
              </div>
              <div className="submit-modal-title-block">
                <h2 className="submit-modal-title">
                  {isTimeExpired ? "Time's Up! Final Submission" : "Submit Test"}
                </h2>
                <p className="submit-modal-subtitle">
                  {isTimeExpired
                    ? "Your exam duration has ended. Review your attempt summary below."
                    : "Review your question attempt status before submitting."}
                </p>
              </div>
            </div>

            <div className="submit-modal-body">
              {/* Quick Glance Stat Cards */}
              <div className="submit-stat-summary-row">
                <div className="submit-stat-summary-card slate">
                  <span className="submit-stat-card-label">Total Questions</span>
                  <span className="submit-stat-card-value">{questions.length}</span>
                </div>
                <div className="submit-stat-summary-card green">
                  <span className="submit-stat-card-label">Answered</span>
                  <span className="submit-stat-card-value">{countAnswered + countAnsweredMarked}</span>
                </div>
                <div className="submit-stat-summary-card red">
                  <span className="submit-stat-card-label">Unanswered</span>
                  <span className="submit-stat-card-value">{countNotAnswered + countNotVisited}</span>
                </div>
                <div className="submit-stat-summary-card purple">
                  <span className="submit-stat-card-label">Marked for Review</span>
                  <span className="submit-stat-card-value">{countMarked + countAnsweredMarked}</span>
                </div>
              </div>

              {/* Detailed Breakdown Table */}
              <div className="submit-table-wrapper">
                <table className="submit-stats-table">
                  <thead>
                    <tr>
                      <th className="th-section">Section</th>
                      <th className="th-answered">Answered</th>
                      <th className="th-not-answered">Not Answered</th>
                      <th className="th-marked">Marked</th>
                      <th className="th-not-visited">Not Visited</th>
                      <th className="th-answered-marked">Answered &amp; Marked</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="td-section">
                        <span className="submit-section-name">{testName}</span>
                      </td>
                      <td><span className="submit-cell-chip green">{countAnswered}</span></td>
                      <td><span className="submit-cell-chip red">{countNotAnswered}</span></td>
                      <td><span className="submit-cell-chip purple">{countMarked}</span></td>
                      <td><span className="submit-cell-chip gray">{countNotVisited}</span></td>
                      <td><span className="submit-cell-chip blue">{countAnsweredMarked}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Warning / Time Expired Notice Card */}
              <div className={`submit-warning-card ${isTimeExpired ? "time-expired-warning" : ""}`}>
                <span className="submit-warning-icon">{isTimeExpired ? "⏰" : "⚠️"}</span>
                <span className="submit-warning-text">
                  {isTimeExpired
                    ? `Time limit reached. All responses are saved. Submitting automatically in ${autoSubmitGraceSeconds}s, or click Submit Test below.`
                    : "Are you sure you want to submit? Once submitted, you cannot change your answers or revisit questions."}
                </span>
              </div>

              <div className="submit-actions">
                {!isTimeExpired && (
                  <button
                    type="button"
                    className="submit-btn-secondary"
                    onClick={() => setShowSubmitModal(false)}
                  >
                    Go Back
                  </button>
                )}
                <button
                  type="button"
                  className="submit-btn-primary"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting
                    ? "Submitting..."
                    : isTimeExpired
                    ? `Submit Test (${autoSubmitGraceSeconds}s)`
                    : "Submit Test"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* by sudz:260925 - Revamped Report Question modal */}
      {showReportModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowReportModal(false);
            setReportSubmitted(false);
            setReportType("");
            setReportDescription("");
          }}
        >
          <div
            className="modal-content report-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={() => {
                setShowReportModal(false);
                setReportSubmitted(false);
                setReportType("");
                setReportDescription("");
              }}
            >
              &times;
            </button>
            {!reportSubmitted ? (
              <>
                <div className="report-modal-header">
                  <h2 className="modal-title">Report Question</h2>
                  <p className="report-modal-subtitle">
                    Found an issue with this question? Let us know so we can fix it.
                  </p>
                </div>
                <div className="report-form-group">
                  <label className="report-label">
                    Report Type <span className="required-star">*</span>
                  </label>
                  <select
                    className="report-select"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <option value="" disabled>Select an option</option>
                    <option value="WRONG_ANSWER">Wrong Answer</option>
                    <option value="AMBIGUOUS_QUESTION">Ambiguous Question</option>
                    <option value="OUTDATED_DATA">Outdated Data</option>
                    <option value="TYPO">Typing Error</option>
                    <option value="FEEDBACK">Feedback</option>
                    <option value="OTHERS">Others</option>
                  </select>
                </div>
                <div className="report-form-group">
                  <label className="report-label">
                    Description <span className="required-star">*</span>
                  </label>
                  <textarea
                    className="report-textarea"
                    rows="4"
                    placeholder="Describe the issue in detail..."
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                  />
                </div>
                <div className="report-actions">
                  <button
                    type="button"
                    className="report-cancel-btn"
                    onClick={() => {
                      setShowReportModal(false);
                      setReportSubmitted(false);
                      setReportType("");
                      setReportDescription("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="report-submit-btn"
                    disabled={reportSubmitting || !reportType || !reportDescription.trim()}
                    onClick={async () => {
                      try {
                        setReportSubmitting(true);
                        await reportTestQuestion(
                          testId,
                          currentQ.id,
                          reportType,
                          reportDescription.trim()
                        );
                        setReportSubmitted(true);
                      } catch (error) {
                        console.error(
                          "Failed to submit report:",
                          error
                        );
                      } finally {
                        setReportSubmitting(false);
                      }
                    }}
                  >
                    {reportSubmitting ? "Submitting..." : "Submit Report"}
                  </button>
                </div>
              </>
            ) : (
              <div className="report-success-msg">
                <div className="report-success-icon">✓</div>
                <h3>Report Submitted</h3>
                <p>Thank you for your feedback! Our team will review this question.</p>
                <button
                  type="button"
                  className="report-submit-btn"
                  onClick={() => {
                    setShowReportModal(false);
                    setReportSubmitted(false);
                    setReportType("");
                    setReportDescription("");
                  }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* by sudz - 260926 - Phase 4: Safe Exit / Navigation Guard Modal */}
      {showExitModal && (
        <div className="modal-overlay" onClick={() => setShowExitModal(false)}>
          <div
            className="modal-content exit-test-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={() => setShowExitModal(false)}
            >
              &times;
            </button>

            <div className="exit-modal-header">
              <div className="exit-modal-icon-badge">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dc2626"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <div className="exit-modal-titles">
                <h2 className="modal-title">Leave Assessment?</h2>
                <p className="exit-modal-subtitle">
                  Your progress for <strong>{testName}</strong> is auto-saved.
                </p>
              </div>
            </div>

            <div className="exit-modal-body">
              <div className="exit-warning-card">
                <span className="submit-warning-icon">⚠️</span>
                <span className="submit-warning-text">
                  Exiting will pause your test. You can resume it anytime on this device, or submit your current responses now.
                </span>
              </div>

              <div className="submit-stat-summary-row exit-stat-summary-row">
                <div className="submit-stat-summary-card slate">
                  <span className="submit-stat-card-label">Total Questions</span>
                  <span className="submit-stat-card-value">{questions.length}</span>
                </div>
                <div className="submit-stat-summary-card green">
                  <span className="submit-stat-card-label">Answered</span>
                  <span className="submit-stat-card-value">
                    {countAnswered + countAnsweredMarked}
                  </span>
                </div>
                <div className="submit-stat-summary-card red">
                  <span className="submit-stat-card-label">Unanswered</span>
                  <span className="submit-stat-card-value">
                    {countNotAnswered + countNotVisited}
                  </span>
                </div>
                <div className="submit-stat-summary-card purple">
                  <span className="submit-stat-card-label">Marked</span>
                  <span className="submit-stat-card-value">
                    {countMarked + countAnsweredMarked}
                  </span>
                </div>
              </div>

              <div className="exit-modal-actions">
                <button
                  type="button"
                  className="exit-btn-resume"
                  onClick={() => setShowExitModal(false)}
                >
                  Resume Test
                </button>
                <button
                  type="button"
                  className="exit-btn-submit"
                  onClick={() => {
                    setShowExitModal(false);
                    setShowSubmitModal(true);
                  }}
                >
                  Submit Test Now
                </button>
                <button
                  type="button"
                  className="exit-btn-danger"
                  onClick={() => handleExitTest(false)}
                >
                  Exit to Tests
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* by sudz - 260926 - Multi-tab Prevention Blocking Overlay */}
      {tabBlocked && (
        <div className="tab-block-overlay">
          <div className="tab-block-modal">
            <div className="tab-block-icon">⚠️</div>
            <h2 className="tab-block-title">Test Active in Another Tab</h2>
            <p className="tab-block-text">{tabBlockedMsg}</p>
            <div className="tab-block-actions">
              <button
                type="button"
                className="tab-block-resume-btn"
                onClick={() => {
                  const currentUserId = localStorage.getItem("user_id") || "";
                  localStorage.setItem(
                    "mtr_active_test_lock",
                    JSON.stringify({
                      tabId,
                      testId: String(testId),
                      userId: currentUserId,
                      timestamp: Date.now(),
                    })
                  );
                  try {
                    if (channelRef.current) {
                      channelRef.current.postMessage({
                        type: "ACQUIRE_LOCK",
                        tabId,
                        testId: String(testId),
                        userId: currentUserId,
                      });
                    }
                  } catch (e) {}
                  setTabBlocked(false);
                }}
              >
                Switch &amp; Resume in This Tab
              </button>
              <button
                type="button"
                className="tab-block-exit-btn"
                onClick={() => {
                  navigate("/tests");
                }}
              >
                Back to My Tests
              </button>
            </div>
          </div>
        </div>
      )}

      {/* by sudz - 261004 - Proctoring Shield (Focus Loss / Screenshot Protection) */}
      {!isReviewMode && isWindowBlurred && (
        <div
          className="proctor-shield-overlay"
          onClick={() => {
            window.focus();
          }}
        >
          <div className="proctor-shield-card">
            <div className="proctor-shield-icon">🛡️</div>
            <h2 className="proctor-shield-title">Assessment Paused</h2>
            <p className="proctor-shield-desc">
              The test window lost focus. To maintain examination integrity, viewing or taking screenshots from other applications is prevented.
            </p>
            <div className="proctor-shield-pulse-wrap">
              <span className="proctor-shield-pulse-dot" />
              <span className="proctor-shield-pulse-text">Click anywhere in this window to resume</span>
            </div>
          </div>
        </div>
      )}

      {/* by sudz - 261016 - Developer Tools / Inspector Blocking Shield */}
      {!isReviewMode && devtoolsOpen && (
        <div
          className="proctor-shield-overlay"
          onClick={() => enterFullscreenRef.current?.()}
        >
          <div className="proctor-shield-card">
            <div className="proctor-shield-icon">🛡️</div>
            <h2 className="proctor-shield-title">Developer Tools Detected</h2>
            <p className="proctor-shield-desc">
              Opening the browser developer tools or network inspector during the
              examination is not permitted. Please close the developer tools to
              resume your test.
            </p>
            <div className="proctor-shield-pulse-wrap">
              <span className="proctor-shield-pulse-dot" />
              <span className="proctor-shield-pulse-text">
                {isFullscreen
                  ? "Waiting for developer tools to close…"
                  : "Click anywhere to enter secure fullscreen"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* by sudz - 261004 - Proctor Auto-Submit Modal (after 5 warnings) */}
      {!isReviewMode && isProctorAutoSubmitting && (
        <div className="proctor-warning-modal-overlay">
          <div className="proctor-warning-modal proctor-autosubmit-modal">
            <div className="proctor-warning-icon">📋</div>
            <h3 className="proctor-warning-title" style={{ color: "#0f172a" }}>
              Assessment Auto-Submitted
            </h3>
            <p className="proctor-warning-message">
              You have reached 5 proctoring warnings (window focus loss / screenshot attempts).
            </p>
            <p className="proctor-warning-subtext" style={{ marginBottom: "16px" }}>
              All your recorded answers and time spent have been preserved. Submitting test...
            </p>
            <div className="proctor-autosubmit-loader">
              <span className="proctor-autosubmit-spinner" />
              <span>Redirecting to test summary...</span>
            </div>
          </div>
        </div>
      )}

      {/* by sudz - 261004 - Proctoring Warning Modal (< 5 warnings) */}
      {!isReviewMode && !isProctorAutoSubmitting && showViolationModal && (
        <div className="proctor-warning-modal-overlay">
          <div className="proctor-warning-modal">
            <div className="proctor-warning-icon">⚠️</div>
            <h3 className="proctor-warning-title">Proctoring Notice</h3>
            <p className="proctor-warning-message">
              {violationNotice || "Navigating away from the active exam window or switching applications is recorded."}
            </p>
            <div className="proctor-warning-stats">
              <span className="proctor-warning-badge">
                Warning <strong>{proctorViolations}</strong> of <strong>5</strong>
              </span>
            </div>
            <p className="proctor-warning-subtext">
              {proctorViolations >= 4
                ? "Caution: One more warning will trigger automatic test submission."
                : "Please remain on this screen. After 5 warnings, your test will be automatically submitted."}
            </p>
            <button
              type="button"
              className="proctor-warning-btn"
              onClick={() => setShowViolationModal(false)}
            >
              I Understand &amp; Resume Test
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestScreen;
